import { z } from 'zod';
import type { UpdateProfileRequest } from '@entities/manufacturer';

const EMPLOYEES = ['XS', 'S', 'M', 'L', 'XL'] as const;

// Spec §5.2 vs §5.3: PUT /manufacturer/profile saves any in-progress state —
// the strict "eco producer must have eco certificates" rule lives in the KYC
// submission gate (entities/manufacturer/lib/kyc.ts), not here. Enforcing it
// at form-save level used to silently block PUT when the user picked an
// ECO_PRODUCER category but hadn't uploaded certs yet.
export const profileFormSchema = z.object({
  companyName: z.string().trim().min(1, 'required').max(256),
  description: z.string().max(16000).optional().nullable(),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2 (2 uppercase letters)')
    .optional()
    .nullable(),
  categories: z.array(z.string().uuid()).default([]),
  // Field names mirror the backend payload (logo/video/certificates/...).
  logo: z.string().uuid().nullable().optional(),
  video: z.string().uuid().nullable().optional(),
  certificates: z.array(z.string().uuid()).default([]),
  ecoCertificates: z.array(z.string().uuid()).default([]),
  ecoProducer: z.boolean().default(false),
  website: z
    .string()
    .trim()
    .url('must be a valid URL')
    .or(z.literal(''))
    .optional()
    .nullable(),
  foundedYear: z
    .number({ invalid_type_error: 'number expected' })
    .int()
    .min(0)
    .max(2100)
    .nullable()
    .optional(),
  employeesRange: z.enum(EMPLOYEES).nullable().optional(),
});

export type ProfileFormValues = z.infer<typeof profileFormSchema>;

export function toUpdateRequest(values: ProfileFormValues): UpdateProfileRequest {
  return {
    companyName: values.companyName.trim(),
    description: values.description?.trim() || null,
    country: values.country || null,
    categories: values.categories,
    // Asymmetric API: PUT expects *MediaId/*MediaIds suffixes even though
    // the GET response uses bare names. See entities/manufacturer types.
    logoMediaId: values.logo ?? null,
    videoMediaId: values.video ?? null,
    certificateMediaIds: values.certificates,
    ecoCertificateMediaIds: values.ecoCertificates,
    ecoProducer: values.ecoProducer,
    website: values.website?.trim() || null,
    foundedYear: values.foundedYear ?? null,
    employeesRange: values.employeesRange ?? null,
  };
}
